import { useCallback } from 'react';
import every from 'lodash/every';
import filter from 'lodash/filter';
import size from 'lodash/size';

import {
  LifestylesSetSelectedLifestylesID,
  UpdateLifestylesSetCacheAction
} from '../../../../../../../../lifestylesSets/lifestylesSetsTypes';

import {
  SubmitSelectedLifestylesToProjectQueryResponse,
  SUBMIT_SELECTED_LIFESTYLES_TO_PROJECT_QUERY
} from '../../../../../../../../projects/queries/submitSelectedLifestylesToProject.query';

import { useSubmitSelectedLifestylesToProject } from '../../../../../../../../projects/hooks/useSubmitSelectedLifestylesToProject';

import {
  SubmitSelectedLifestylesToTaskQueryResponse,
  SUBMIT_SELECTED_LIFESTYLES_TO_TASK_QUERY
} from '../../../../../../../../tasks/queries/submitSelectedLifestylesToTask.query';

import { useSubmitSelectedLifestylesToTask } from '../../../../../../../../tasks/hooks/useSubmitSelectedLifestylesToTask';

import { SelectProjectAndTaskModalButtonOnSubmitAction } from '../../../../../../../../common/components/modalButtons/SelectProjectAndTaskModalButton';

import { LifestylesSetCache } from '../../../../../../../../lifestylesSets/LifestylesSetCache';

import { SelectedLifestylesSidebarAttachModalButtonLifestylesSet } from '../../SelectedLifestylesSidebarAttachModalButton.types';

interface UseSelectedLifestylesSidebarAttachModalButtonOptions {
  selectedLifestyleIds: LifestylesSetSelectedLifestylesID[];
  onClose: () => void;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<SelectedLifestylesSidebarAttachModalButtonLifestylesSet>;
}

function useSelectedLifestylesSidebarAttachModalButton({
  selectedLifestyleIds,
  onClose,
  updateLifestylesSetCache
}: UseSelectedLifestylesSidebarAttachModalButtonOptions) {
  const {
    submitSelectedLifestylesToProjectErrorMessage,
    submitSelectedLifestylesToProjectLoading,
    submitSelectedLifestylesToProject,
    submitSelectedLifestylesToProjectReset
  } = useSubmitSelectedLifestylesToProject<SubmitSelectedLifestylesToProjectQueryResponse>(
    {
      query: SUBMIT_SELECTED_LIFESTYLES_TO_PROJECT_QUERY,
      cacheKeys: [LifestylesSetCache.showCacheKey()]
    }
  );

  const {
    submitSelectedLifestylesToTaskErrorMessage,
    submitSelectedLifestylesToTaskLoading,
    submitSelectedLifestylesToTask,
    submitSelectedLifestylesToTaskReset
  } = useSubmitSelectedLifestylesToTask<SubmitSelectedLifestylesToTaskQueryResponse>(
    {
      query: SUBMIT_SELECTED_LIFESTYLES_TO_TASK_QUERY,
      cacheKeys: [LifestylesSetCache.showCacheKey()]
    }
  );

  const handleAfterSubmit = useCallback<() => void>(() => {
    updateLifestylesSetCache({
      updateFunction: (prevLifestyleSet) => ({
        ...prevLifestyleSet,
        selectedLifestyles:
          size(selectedLifestyleIds) > 0
            ? filter(
                prevLifestyleSet.selectedLifestyles,
                (selectedLifestyles) =>
                  every(
                    selectedLifestyleIds,
                    (id) => id !== selectedLifestyles.id
                  )
              )
            : []
      })
    });
    onClose();
  }, [onClose, selectedLifestyleIds, updateLifestylesSetCache]);

  const handleSubmit =
    useCallback<SelectProjectAndTaskModalButtonOnSubmitAction>(
      ({ projectUuid, taskUuid }) => {
        if (taskUuid) {
          return submitSelectedLifestylesToTask({
            uuid: taskUuid,
            selectedLifestyleIds
          }).then(() => {
            handleAfterSubmit();
          });
        }
        return submitSelectedLifestylesToProject({
          uuid: projectUuid,
          selectedLifestyleIds
        }).then(() => {
          handleAfterSubmit();
        });
      },
      [
        submitSelectedLifestylesToProject,
        selectedLifestyleIds,
        submitSelectedLifestylesToTask,
        handleAfterSubmit
      ]
    );

  const handleClose = useCallback<() => void>(() => {
    submitSelectedLifestylesToProjectReset();
    submitSelectedLifestylesToTaskReset();
  }, [
    submitSelectedLifestylesToProjectReset,
    submitSelectedLifestylesToTaskReset
  ]);

  return {
    isLoading:
      submitSelectedLifestylesToProjectLoading ||
      submitSelectedLifestylesToTaskLoading,
    errorMessage:
      submitSelectedLifestylesToProjectErrorMessage ||
      submitSelectedLifestylesToTaskErrorMessage,
    handleSubmit,
    handleClose
  };
}

export default useSelectedLifestylesSidebarAttachModalButton;
